import { Chip, IconButton, Tabs } from '@dixa/lib-shared-habla';
import { AddLocaleButton } from 'articleEditor/components/localePicker/addLocalButton';
import {
  useAddTranslation,
  useEditorLanguages,
} from 'articleEditor/components/localePicker/useLocales';
import { ArticleDataContext } from 'articleEditor/contexts/articleDataProvider';
import { useIsTranslating } from 'articleEditor/hooks/translations/useIsTranslating';
import { TranslationDeleteModal } from 'articleEditor/modals/translationDeleteModal';
import { LanguageModel } from 'lib-knowledge-management-common/hooks/useManagementLanguages';
import { useModal } from 'lib-knowledge-modal';
import { ComponentProps, useCallback, useContext, useMemo } from 'react';

import s from './localePicker.module.scss';

type TabsArray = ComponentProps<typeof Tabs>['tabs'];
type UseBaseLocalePickerProps = {
  tabRenderer: (
    language: LanguageModel,
    renderDeleteButton: (language: LanguageModel) => JSX.Element,
    onDeleteTranslation: (locale: LanguageModel) => void,
  ) => JSX.Element;
};

export const useBaseLocalePicker = ({
  tabRenderer,
}: UseBaseLocalePickerProps): JSX.Element => {
  const { showModal } = useModal();
  const articleData = useContext(ArticleDataContext);
  const {
    setCurrentLanguage,
    translatedLanguages,
    availableLanguages,
    defaultLanguage,
    selectedLanguage,
  } = useEditorLanguages();
  const [addTranslation, addTranslationResult] = useAddTranslation();
  const isTranslating = useIsTranslating();

  const onDeleteTranslation = useCallback(
    (locale: LanguageModel) => {
      showModal(TranslationDeleteModal, {
        articleId: articleData.id,
        languageName: locale.description,
        languageId: locale.languageId,
        onAccept: () => {
          // Find first language in translatedLanguages that hasn't been deleted and switch to that
          const newLanguage = translatedLanguages.find(
            (language) => language.languageId !== locale.languageId,
          );
          if (!newLanguage) throw new Error('No languages left');
          setCurrentLanguage(newLanguage.languageId);
        },
      });
    },
    [articleData.id, showModal, setCurrentLanguage, translatedLanguages],
  );

  const renderDeleteButton = useCallback(
    (language: LanguageModel) => {
      return (
        <IconButton
          variant="tertiary"
          icon="close"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDeleteTranslation(language);
          }}
        />
      );
    },
    [onDeleteTranslation],
  );

  const languageTabs = useMemo((): TabsArray => {
    return translatedLanguages.map((language: LanguageModel) => {
      return {
        id: language.languageId,
        label: language.description,
        disabled: addTranslationResult.loading || isTranslating,
        slotEnd: (
          <>
            {language.languageId === defaultLanguage.languageId && (
              <Chip>default</Chip>
            )}

            {language.languageId === selectedLanguage.languageId &&
              translatedLanguages.length > 1 &&
              tabRenderer(language, renderDeleteButton, onDeleteTranslation)}
          </>
        ),
      };
    });
  }, [
    addTranslationResult.loading,
    defaultLanguage.languageId,
    selectedLanguage.languageId,
    translatedLanguages,
    isTranslating,
    tabRenderer,
    onDeleteTranslation,
    renderDeleteButton,
  ]);

  const onTabChange = useCallback(
    (id: string) => {
      setCurrentLanguage(id as LanguageModel['languageId']);
    },
    [setCurrentLanguage],
  );

  return (
    <div className={s.container}>
      <Tabs
        className={s.tabs}
        tabClassName={s.tab}
        tabs={languageTabs}
        onChange={onTabChange}
        selectedId={selectedLanguage.languageId || defaultLanguage.languageId}
      />

      {availableLanguages.length > 0 && !articleData.isSyncedWithElevio && (
        <AddLocaleButton
          disabled={addTranslationResult.loading || isTranslating}
          availableLanguages={availableLanguages}
          addTranslation={addTranslation}
        />
      )}
    </div>
  );
};
